import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Heart, Bookmark, Download, Calendar, MessageSquare, EyeOff, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { getCurriculum } from '../constants/curricula';
import {
  hasLocalLike, addLocalLike,
  hasLocalSave, toggleLocalSave,
} from '../lib/localUserState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResourceDetail = () => {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentName, setCommentName] = useState('');
  const [commentAnon, setCommentAnon] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    fetchResource();
    fetchComments();
    setIsLiked(hasLocalLike(id));
    setIsSaved(hasLocalSave(id));
  }, [id]);

  const fetchResource = async () => {
    try {
      const { data } = await axios.get(`${API}/resources/${id}`);
      setResource(data);
    } catch (error) {
      toast.error('Failed to load resource');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API}/resources/${id}/comments`);
      setComments(data);
    } catch (error) {
      console.error('Failed to load comments');
    }
  };

  const handleLike = async () => {
    if (hasLocalLike(id)) {
      toast.info('You already liked this');
      return;
    }
    try {
      await axios.post(`${API}/resources/${id}/like`);
      addLocalLike(id);
      setIsLiked(true);
      setResource((r) => r ? { ...r, likes_count: r.likes_count + 1 } : r);
    } catch (error) {
      toast.error('Failed to like resource');
    }
  };

  const handleSave = () => {
    const nowSaved = toggleLocalSave(id);
    setIsSaved(nowSaved);
    toast.success(nowSaved ? 'Saved on this device' : 'Removed from saved');
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!commentAnon && !commentName.trim()) {
      toast.error('Enter your name or toggle "Post anonymously"');
      return;
    }
    setSubmitting(true);
    try {
      const author_name = commentAnon ? 'Anonymous' : commentName.trim();
      await axios.post(`${API}/resources/${id}/comments`, { content: newComment, author_name });
      setNewComment('');
      toast.success('Comment added');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}${resource.file_url}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="text-center py-20">
          <h2 className="text-2xl font-semibold mb-4">Resource not found</h2>
          <Link to="/resources">
            <Button>Browse Resources</Button>
          </Link>
        </div>
      </div>
    );
  }

  const rCurr = getCurriculum(resource.curriculum);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article>
          <header className="mb-8 pb-8 border-b border-border">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {rCurr && (
                <span className={`text-xs px-2 py-1 rounded font-mono ${rCurr.badge}`}>
                  {resource.curriculum}{resource.level ? ` · ${resource.level}` : ''}
                </span>
              )}
              <span className="text-xs uppercase tracking-[0.1em] font-mono text-primary">{resource.subject}</span>
              {resource.tags && resource.tags.length > 0 && (
                <>
                  <span className="text-foreground/40">•</span>
                  {resource.tags.slice(0, 2).map((tag, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 rounded bg-accent text-foreground/75">
                      {tag}
                    </span>
                  ))}
                </>
              )}
            </div>

            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6 leading-tight" data-testid="resource-detail-title">
              {resource.title}
            </h1>

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-medium">
                  {(resource.uploader_name || 'A')[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{resource.uploader_name || 'Anonymous'}</p>
                  <p className="text-sm text-foreground/65 flex items-center gap-1">
                    <Calendar className="h-3 w-3" strokeWidth={1.5} />
                    {formatDistanceToNow(new Date(resource.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={handleLike}
                  className={isLiked ? 'text-red-500 border-red-200' : ''}
                  data-testid="resource-detail-like-button"
                >
                  <Heart className="h-4 w-4 mr-2" strokeWidth={1.5} fill={isLiked ? 'currentColor' : 'none'} />
                  {resource.likes_count} {resource.likes_count === 1 ? 'Like' : 'Likes'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSave}
                  className={isSaved ? 'text-primary border-primary/40' : ''}
                  data-testid="resource-detail-save-button"
                >
                  <Bookmark className="h-4 w-4 mr-2" strokeWidth={1.5} fill={isSaved ? 'currentColor' : 'none'} />
                  {isSaved ? 'Saved' : 'Save'}
                </Button>
                <Button onClick={handleDownload} data-testid="resource-detail-download-button">
                  <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Download
                </Button>
              </div>
            </div>
          </header>

          {resource.description && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">About this resource</h2>
              <p className="text-foreground/80 leading-relaxed">{resource.description}</p>
            </div>
          )}

          <div className="mb-8 p-6 bg-surface rounded-lg border border-border">
            <h3 className="font-medium mb-2">File Information</h3>
            <div className="text-sm text-foreground/70 space-y-1">
              <p>Filename: {resource.original_filename}</p>
              <p>Type: {resource.file_type.toUpperCase()}</p>
            </div>
          </div>
        </article>

        <section className="mt-12 pt-12 border-t border-border">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
            <MessageSquare className="h-6 w-6" strokeWidth={1.5} />
            Comments ({comments.length})
          </h2>

          <form onSubmit={handleCommentSubmit} className="mb-8 space-y-4" data-testid="comment-form">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts..."
              rows={4}
              data-testid="comment-input"
            />

            <div className="rounded-lg border border-border bg-surface p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-accent flex items-center justify-center">
                  {commentAnon
                    ? <EyeOff className="h-4 w-4 text-primary" strokeWidth={1.5} />
                    : <UserIcon className="h-4 w-4 text-primary" strokeWidth={1.5} />}
                </div>
                <div className="flex-1">
                  <Label htmlFor="comment-anon" className="text-sm font-medium cursor-pointer">Post anonymously</Label>
                  <p className="text-xs text-foreground/65 mt-0.5">
                    {commentAnon ? 'Your name won\'t be shown.' : 'Your name will appear next to this comment.'}
                  </p>
                </div>
                <Checkbox
                  id="comment-anon"
                  checked={commentAnon}
                  onCheckedChange={(v) => setCommentAnon(Boolean(v))}
                  data-testid="comment-anonymous-toggle"
                />
              </div>
              {!commentAnon && (
                <div className="mt-3">
                  <Input
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    placeholder="Your name"
                    required={!commentAnon}
                    data-testid="comment-name-input"
                  />
                </div>
              )}
            </div>

            <Button type="submit" disabled={submitting || !newComment.trim()} data-testid="comment-submit-button">
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </form>

          <div className="space-y-6" data-testid="comments-list">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-4" data-testid={`comment-${comment.id}`}>
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-medium flex-shrink-0">
                  {(comment.user_name || 'A')[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{comment.user_name || 'Anonymous'}</span>
                    <span className="text-xs text-foreground/65">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-foreground/85 leading-relaxed">{comment.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
